import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  Control,
  FieldArrayWithId,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Collapse,
  Chip,
  Stack,
  Autocomplete,
  Button,
  TextField,
  Skeleton,
  CircularProgress,
  IconButton,
  Alert,
} from "@mui/material";
import {
  NumericEntryField,
  NumericDisplayField,
} from "../../components/common/CustomFields";
import {
  Search as SearchIcon,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import { TypedSelectOption } from "../../types/common/selectOptions";
import stringUtils from "../../utils/stringUtils";
import statusUtils from "../../utils/statusUtils";
import { paginationDefaults } from "../../config/settingsDefaults";
import "../../styles/chipOverrides";
import { DynamicRequestFilter } from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";

type primaryTargetOption = TypedSelectOption<
  "prim_target",
  {
    number: string;
    name: string;
    catalog_name: string | null;
    risk_level: string;
    revision: string;
    storage_unit_id: string;
    storage_unit_symbol: string;
    storage_unit_conversion: number;
  }
>;
type targetBomOption = TypedSelectOption<
  "bom",
  {
    version_name: string;
    version_number: number;
  }
>;

interface ComponentData {
  compId: string;
  itemId: string;
  itemNum: string;
  itemName: string;
  itemRev: string;
  unitId: string;
  unitSymbol: string;
  unitConversion: number;
  buildAsNeeded: boolean;
  quantity: number;
  offset: number;
}

const InventoryDetails: React.FC<{
  inventoryInfo: {
    avail: { [key: string]: number };
    total: { [key: string]: number };
  };
  uomConversion: number;
  uom: string;
}> = ({ inventoryInfo, uomConversion, uom }) => {
  const [availQuant, totalQuant] = (() => {
    let a = 0;
    let t = 0;
    for (let x in inventoryInfo["avail"]) {
      a += inventoryInfo["avail"][x];
    }
    for (let x in inventoryInfo["total"]) {
      t += inventoryInfo["total"][x];
    }
    return [a, t];
  })();
  return (
    <Stack direction="column">
      <Typography variant="body1">
        Available: {(availQuant / uomConversion).toFixed(4)}x {uom}
      </Typography>
      <Typography variant="body1">
        Total On Hand: {(totalQuant / uomConversion).toFixed(4)}x {uom}
      </Typography>
    </Stack>
  );
};

const ComponentLine: React.FC<{
  level: number;
  thisCompData: ComponentData;
  parentItem: string;
  parentQuant: number;
  totalNeeded: { [key: string]: number };
  setTotalNeeded: (itemId: string, quantity: number) => void;
  generationId: number;
}> = ({
  level,
  thisCompData,
  parentItem,
  parentQuant,
  totalNeeded,
  setTotalNeeded,
  generationId,
}) => {
  const lineQuantNeeded = useMemo(
    () => (thisCompData.quantity * parentQuant) / thisCompData.offset,
    [thisCompData.quantity, thisCompData.offset, parentQuant],
  );

  const hasReportedRef = useRef<{ generation: number; reported: boolean }>({
    generation: -1,
    reported: false,
  });

  useEffect(() => {
    if (hasReportedRef.current.generation !== generationId) {
      hasReportedRef.current = { generation: generationId, reported: false };
    }
    if (!hasReportedRef.current.reported) {
      console.log("SETTING LINE TOTAL NEEDED", thisCompData.itemId);
      setTotalNeeded(thisCompData.itemId, lineQuantNeeded);
      hasReportedRef.current.reported = true;
    }
  }, [generationId, thisCompData.itemId, lineQuantNeeded, setTotalNeeded]);

  const lineTotalNeeded = useMemo(() => {
    console.log("GETTING LINE TOTAL NEEDED", thisCompData.itemId);
    return (totalNeeded[thisCompData.itemId] || 0).toFixed(4) || "0.0000";
  }, [totalNeeded, thisCompData.itemId]);

  const [subCompsOpen, setSubCompsOpen] = useState<boolean>(false);
  const [selectedSubBom, setSelectedSubBom] = useState<targetBomOption | null>(
    null,
  );
  const {
    data: bomsResponse,
    isLoading: bomsLoading,
    isFetching: bomsFetching,
    error: bomsError,
    isPlaceholderData: bomsPlaceholder,
  } = useTSQDynamicList({
    page: 0,
    page_size: 1000,
    primary: "bom_revisions",
    alt_name: "bom",
    primary_fields: ["id", "version_name", "version_number"],
    joins: [],
    sorts: [
      {
        table: "bom_revisions",
        alt_name: "bom",
        field: "version_name",
        asc: false,
      },
    ],
    filters: [
      [
        {
          table: "bom_revisions",
          alt_name: "bom",
          field: "item_id",
          operator: "==",
          value: thisCompData.itemId,
          is_not: false,
          inclusive_end: false,
          inclusive_start: false,
          case_insensitive: false,
        },
      ],
    ],
    subqueries: [],
  });

  const boms: targetBomOption[] = useMemo(() => {
    if (bomsResponse?.items) {
      return bomsResponse.items.map((val) => ({
        label: val["bom.version_name"].substr(9),
        type: "bom",
        value: {
          id: val["bom.id"],
          version_name: val["bom.version_name"],
          version_number: val["bom.version_number"],
        },
      }));
    } else {
      return [];
    }
  }, [bomsResponse]);

  useEffect(() => {
    if (boms.length > 0) {
      setSelectedSubBom(boms[0]);
      setSubCompsOpen(true);
    } else {
      setSelectedSubBom(null);
      setSubCompsOpen(false);
    }
  }, [boms]);

  const bomCompFilters: DynamicRequestFilter[][] = useMemo(() => {
    if (selectedSubBom) {
      return [
        [
          {
            table: "bom_components",
            alt_name: "comp",
            field: "bom_revision_id",
            operator: "==",
            value: selectedSubBom.value.id,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ];
    } else {
      return [];
    }
  }, [selectedSubBom]);

  const {
    data: bomCompsResponse,
    isLoading: bomCompsLoading,
    isFetching: bomCompsFetching,
    error: bomCompsError,
    isPlaceholderData: bomCompsPlaceholder,
  } = useTSQDynamicList({
    page: 0,
    page_size: 1000,
    primary: "bom_components",
    alt_name: "comp",
    primary_fields: ["id", "quantity", "offset", "line", "build_as_needed"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "number", "name", "revision"],
        condition: {
          remote_table: "bom_components",
          alt_name: "comp",
          remote_field: "component_item_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "uom_units",
        alt_name: "unit",
        fields: ["id", "unit_symbol", "conversion_factor"],
        condition: {
          remote_table: "bom_components",
          alt_name: "comp",
          remote_field: "uom_unit_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    sorts: [
      {
        table: "bom_components",
        alt_name: "comp",
        field: "line",
        asc: true,
      },
    ],
    filters: bomCompFilters,
    subqueries: [],
  });

  const subBomComps: ComponentData[] = useMemo(() => {
    if (bomCompsResponse?.items && selectedSubBom) {
      return bomCompsResponse.items.map((val) => ({
        compId: val["comp.id"],
        itemId: val["item.id"],
        itemNum: val["item.number"],
        itemName: val["item.name"],
        itemRev: val["item.revision"],
        unitId: val["unit.id"],
        unitSymbol: val["unit.unit_symbol"],
        unitConversion: val["unit.conversion_factor"],
        buildAsNeeded: val["comp.build_as_needed"],
        quantity: parseFloat(val["comp.quantity"]),
        offset: parseFloat(val["comp.offset"]),
      }));
    } else {
      return [];
    }
  }, [bomCompsResponse, selectedSubBom]);

  const {
    data: inventoryResponse,
    isLoading: inventoryLoading,
    isFetching: inventoryFetching,
    error: inventoryError,
  } = useTSQDynamicList({
    page: 0,
    page_size: 1000,
    primary: "inventory_positions",
    alt_name: "pos",
    primary_fields: ["id", "quantity_on_hand"],
    joins: [
      {
        table: "inventory_statuses",
        alt_name: "status",
        fields: ["id", "allow_picking"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "pos",
          remote_field: "status_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "locations",
        alt_name: "loc",
        fields: ["id"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "pos",
          remote_field: "location_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "inventory_lots",
        alt_name: "lot",
        fields: ["id", "lot_number"],
        condition: {
          remote_table: "inventory_positions",
          alt_name: "pos",
          remote_field: "inventory_lot_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    sorts: [],
    filters: [
      [
        {
          table: "inventory_positions",
          alt_name: "pos",
          field: "quantity_on_hand",
          operator: ">",
          value: 0,
          is_not: false,
          inclusive_end: false,
          inclusive_start: false,
          case_insensitive: false,
        },
        {
          table: "inventory_lots",
          alt_name: "lot",
          field: "item_id",
          operator: "==",
          value: thisCompData.itemId,
          is_not: false,
          inclusive_end: false,
          inclusive_start: false,
          case_insensitive: false,
        },
      ],
    ],
    subqueries: [],
  });

  const inventoryDetails: {
    avail: { [key: string]: number };
    total: { [key: string]: number };
  } = useMemo(() => {
    if (inventoryResponse?.items) {
      const retVal: {
        avail: { [key: string]: number };
        total: { [key: string]: number };
      } = { avail: {}, total: {} };
      inventoryResponse.items.forEach((val, ind) => {
        if (retVal["total"][val["lot.lot_number"]] === undefined) {
          retVal["total"][val["lot.lot_number"]] = 0;
        }
        retVal["total"][val["lot.lot_number"]] += parseFloat(
          val["pos.quantity_on_hand"],
        );
        if (val["status.allow_picking"]) {
          if (retVal["avail"][val["lot.lot_number"]] === undefined) {
            retVal["avail"][val["lot.lot_number"]] = 0;
          }
          retVal["avail"][val["lot.lot_number"]] += parseFloat(
            val["pos.quantity_on_hand"],
          );
        }
      });
      return retVal;
    } else {
      return { avail: {}, total: {} };
    }
  }, [inventoryResponse]);

  return (
    <>
      <TableRow>
        <TableCell width="5%">
          <Box display="flex" gap={0.5} justifyItems="center">
            <Typography variant="body1">{level}</Typography>
            {boms.length > 0 && (
              <IconButton onClick={() => setSubCompsOpen((prev) => !prev)}>
                {subCompsOpen ? <ArrowDownward /> : <ArrowUpward />}
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell width="20%">
          <Stack direction="column" gap={0.5} sx={{ pl: level * 2 }}>
            <Typography variant="body1">{thisCompData.itemNum}</Typography>
            <Typography variant="caption" flexWrap="wrap">
              {thisCompData.itemName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell width="15%">
          {boms.length > 0 ? (
            <Autocomplete
              value={selectedSubBom}
              onChange={(e, v) => setSelectedSubBom(v)}
              options={boms}
              getOptionLabel={(opt) => opt?.label || ""}
              isOptionEqualToValue={(opt, val) =>
                opt.value.id === val?.value.id
              }
              fullWidth
              renderInput={(params) => <TextField {...params} label="BOM" />}
            />
          ) : (
            <Typography variant="body1">{thisCompData.itemRev}</Typography>
          )}
        </TableCell>
        <TableCell width="15%">
          <Stack direction="column" gap={0.5}>
            <Typography variant="body1">
              {(
                (thisCompData.quantity * parentQuant) /
                thisCompData.offset
              ).toFixed(4)}
              x {thisCompData.unitSymbol}
            </Typography>
            <Typography variant="caption">
              {thisCompData.quantity}x {thisCompData.unitSymbol} Per{" "}
              {thisCompData.offset}x {parentItem}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell width="15%">
          <Typography variant="body1">
            {lineTotalNeeded}x {thisCompData.unitSymbol}
          </Typography>
        </TableCell>
        <TableCell width="30%">
          {thisCompData.buildAsNeeded ? (
            <Typography variant="body1">
              Component built as needed, no stock kept.
            </Typography>
          ) : inventoryFetching ? (
            <Stack direction="column">
              <Typography variant="body1">
                Loading Component Inventory Data...
              </Typography>
              <CircularProgress />
            </Stack>
          ) : (
            <InventoryDetails
              inventoryInfo={inventoryDetails}
              uomConversion={thisCompData.unitConversion}
              uom={thisCompData.unitSymbol}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0 }}>
          <Collapse in={subCompsOpen}>
            <Table size="small">
              <TableBody>
                {!selectedSubBom ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h4">
                        Please a BOM to View Component Data
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : bomCompsFetching ? (
                  <>
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Loading Components, Please Wait... <CircularProgress />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" gap={1}>
                          <Typography variant="body1">
                            <Skeleton variant="text" />
                          </Typography>
                          <Typography variant="caption">
                            <Skeleton variant="text" />
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" gap={1}>
                          <Typography variant="body1">
                            <Skeleton variant="text" />
                          </Typography>
                          <Typography variant="body1">
                            <Skeleton variant="text" />
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" gap={1}>
                          <Typography variant="body1">
                            <Skeleton variant="text" />
                          </Typography>
                          <Typography variant="body1">
                            <Skeleton variant="text" />
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          <Skeleton variant="text" />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  </>
                ) : subBomComps.length === 0 ? (
                  <Alert severity="error">
                    Error loading BOM components for {thisCompData.itemNum}
                  </Alert>
                ) : (
                  subBomComps.map((val, ind) => (
                    <ComponentLine
                      key={ind}
                      level={level + 1}
                      thisCompData={val}
                      parentItem={thisCompData.itemNum}
                      parentQuant={
                        (thisCompData.quantity * parentQuant) /
                        thisCompData.offset
                      }
                      totalNeeded={totalNeeded}
                      setTotalNeeded={setTotalNeeded}
                      generationId={generationId}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PageDemandForecast: React.FC = () => {
  const { user } = useAuth();
  const [targetQuant, setTargetQuant] = useState<number>(0);
  const [targetDate, setTargetDate] = useState<string>(
    new Date().toLocaleDateString("en-US"),
  );

  const [selectedItem, setSelectedItem] = useState<primaryTargetOption | null>(
    null,
  );
  const [selectedBom, setSelectedBom] = useState<targetBomOption | null>(null);
  const handleItemChange = (event: any, value: primaryTargetOption | null) => {
    setSelectedBom(null);
    setSelectedItem(value);
  };

  const totalNeededRef = useRef<{ [key: string]: number }>({});
  const [totalNeeded, setTotalNeeded] = useState<{
    [key: string]: number;
  }>({});

  const generationIdRef = useRef(0);

  useEffect(() => {
    generationIdRef.current += 1;
    totalNeededRef.current = {};
    setTotalNeeded({});
  }, [selectedBom, targetQuant]);

  const handleTotalNeededUpdate = useCallback(
    (itemId: string, quantity: number) => {
      totalNeededRef.current = {
        ...totalNeededRef.current,
        [itemId]: (totalNeededRef.current[itemId] || 0) + quantity,
      };
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(() => {
        setTotalNeeded({ ...totalNeededRef.current });
      }, 50);
    },
    [],
  );

  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    isFetching: itemsFetching,
    error: itemsError,
    isPlaceholderData: itemsPlaceholder,
  } = useTSQDynamicList({
    page: 0,
    page_size: 1000,
    primary: "items",
    alt_name: "item",
    primary_fields: [
      "id",
      "number",
      "name",
      "catalog_name",
      "risk_level",
      "revision",
    ],
    joins: [
      {
        table: "uom_units",
        alt_name: "unit",
        fields: ["id", "unit_symbol", "conversion_factor"],
        condition: {
          remote_table: "items",
          alt_name: "item",
          remote_field: "storage_uom_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: [
      [
        {
          table: "items",
          alt_name: "item",
          field: "item_type",
          operator: "==",
          value: ["FINISHED_GOOD", "SUB_ASSEMBLY"],
          is_not: false,
          inclusive_end: false,
          inclusive_start: false,
          case_insensitive: false,
        },
      ],
    ],
    sorts: [
      {
        table: "items",
        alt_name: "item",
        field: "catalog_name",
        asc: true,
      },
      {
        table: "items",
        alt_name: "item",
        field: "number",
        asc: true,
      },
    ],
    subqueries: [],
  });

  const items: primaryTargetOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      return itemsResponse.items.map((val) => ({
        label: `${
          val["item.catalog_name"]
            ? val["item.catalog_name"]
            : val["item.number"]
        } - ${val["item.name"]}`,
        type: "prim_target",
        value: {
          id: val["item.id"],
          number: val["item.number"],
          name: val["item.name"],
          catalog_name: val["item.catalog_name"],
          risk_level: val["item.risk_level"],
          revision: val["item.revision"],
          storage_unit_id: val["unit.id"],
          storage_unit_symbol: val["unit.unit_symbol"],
          storage_unit_conversion: val["unit.conversion_factor"],
        },
      }));
    } else {
      return [];
    }
  }, [itemsResponse]);

  const bomFilters: DynamicRequestFilter[][] = useMemo(() => {
    if (selectedItem) {
      return [
        [
          {
            table: "bom_revisions",
            alt_name: "bom",
            field: "item_id",
            operator: "==",
            value: selectedItem.value.id,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ];
    } else {
      return [];
    }
  }, [selectedItem]);

  const {
    data: bomsResponse,
    isLoading: bomsLoading,
    isFetching: bomsFetching,
    error: bomsError,
    isPlaceholderData: bomsPlaceholder,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "bom_revisions",
      alt_name: "bom",
      primary_fields: ["id", "version_name", "version_number"],
      joins: [],
      sorts: [
        {
          table: "bom_revisions",
          alt_name: "bom",
          field: "version_name",
          asc: false,
        },
      ],
      filters: bomFilters,
      subqueries: [],
    },
    !!selectedItem && bomFilters.length > 0,
  );

  const boms: targetBomOption[] = useMemo(() => {
    if (bomsResponse?.items) {
      return bomsResponse.items.map((val) => ({
        label: val["bom.version_name"],
        type: "bom",
        value: {
          id: val["bom.id"],
          version_name: val["bom.version_name"],
          version_number: val["bom.version_number"],
        },
      }));
    } else {
      return [];
    }
  }, [bomsResponse]);

  const bomCompFilters: DynamicRequestFilter[][] = useMemo(() => {
    if (selectedBom) {
      return [
        [
          {
            table: "bom_components",
            alt_name: "comp",
            field: "bom_revision_id",
            operator: "==",
            value: selectedBom.value.id,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ];
    } else {
      return [];
    }
  }, [selectedBom]);

  const {
    data: bomCompsResponse,
    isLoading: bomCompsLoading,
    isFetching: bomCompsFetching,
    error: bomCompsError,
    isPlaceholderData: bomCompsPlaceholder,
  } = useTSQDynamicList({
    page: 0,
    page_size: 1000,
    primary: "bom_components",
    alt_name: "comp",
    primary_fields: ["id", "quantity", "offset", "line", "build_as_needed"],
    joins: [
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "number", "name", "revision"],
        condition: {
          remote_table: "bom_components",
          alt_name: "comp",
          remote_field: "component_item_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "uom_units",
        alt_name: "unit",
        fields: ["id", "unit_symbol", "conversion_factor"],
        condition: {
          remote_table: "bom_components",
          alt_name: "comp",
          remote_field: "uom_unit_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    sorts: [
      {
        table: "bom_components",
        alt_name: "comp",
        field: "line",
        asc: true,
      },
    ],
    filters: bomCompFilters,
    subqueries: [],
  });

  const mainBomComps: ComponentData[] = useMemo(() => {
    if (bomCompsResponse?.items && selectedBom) {
      return bomCompsResponse.items.map((val) => ({
        compId: val["comp.id"],
        itemId: val["item.id"],
        itemNum: val["item.number"],
        itemName: val["item.name"],
        itemRev: val["item.revision"],
        unitId: val["unit.id"],
        unitSymbol: val["unit.unit_symbol"],
        unitConversion: val["unit.conversion_factor"],
        buildAsNeeded: val["comp.build_as_needed"],
        quantity: parseFloat(val["comp.quantity"]),
        offset: parseFloat(val["comp.offset"]),
      }));
    } else {
      return [];
    }
  }, [bomCompsResponse, selectedBom]);

  return (
    <Stack direction="column" gap={1}>
      <Box display="flex" gap={1} justifyContent={"space-between"}>
        <Typography variant="h4" component="h1">
          Demand Forecasting
        </Typography>
        {user?.is_superuser && (
          <Button
            variant="outlined"
            color="info"
            onClick={() => {
              console.log("Items", items);
              console.log("Boms", boms);
              console.log("Total Needed", totalNeeded);
            }}
          ></Button>
        )}
      </Box>
      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" gap={1}>
          <Autocomplete
            value={selectedItem}
            options={items}
            onChange={(e, v) => handleItemChange(e, v)}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value.id === val?.value.id}
            fullWidth
            renderInput={(params) => <TextField {...params} label="Item" />}
          />
          <Autocomplete
            value={selectedBom}
            options={boms}
            onChange={(e, v) => setSelectedBom(v)}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value.id === val?.value.id}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="BOM"
                placeholder={
                  !selectedItem
                    ? "Select an Item to Load Available BOMs"
                    : "Select BOM"
                }
              />
            )}
            disabled={!selectedItem}
          />
          <NumericEntryField
            value={targetQuant}
            onChange={setTargetQuant}
            decimalPlaces={0}
            isCurrency={false}
            label={"Target Quantity"}
            textFieldProps={{
              fullWidth: true,
              placeholder: !selectedItem
                ? "Select an Item First"
                : !selectedBom
                  ? "Select a BOM First"
                  : "Enter Target Quantity",
            }}
            disabled={!selectedItem || !selectedBom}
          />
        </Box>
      </Paper>
      {/* <Stack direction="column" gap={0.5}>
        <Chip
          label="itemsLoading"
          clickable={false}
          color={itemsLoading ? "error" : "success"}
          size="small"
        />

        <Chip
          label="itemsFetching"
          clickable={false}
          color={itemsFetching ? "error" : "success"}
          size="small"
        />

        <Chip
          label="bomsLoading"
          clickable={false}
          color={bomsLoading ? "error" : "success"}
          size="small"
        />

        <Chip
          label="bomsFetching"
          clickable={false}
          color={bomsFetching ? "error" : "success"}
          size="small"
        />

        <Chip
          label="mainBomCompsLoading"
          clickable={false}
          color={bomCompsLoading ? "error" : "success"}
          size="small"
        />

        <Chip
          label="mainBomCompsFetching"
          clickable={false}
          color={bomCompsFetching ? "error" : "success"}
          size="small"
        />
      </Stack> */}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="5%">BOM Level</TableCell>
              <TableCell width="20%">Item</TableCell>
              <TableCell width="15%">Revision</TableCell>
              <TableCell width="15%">Quantity Needed Per Line</TableCell>
              <TableCell width="15%">
                Total Component Quantity For Build
              </TableCell>
              <TableCell width="30%">Inventory</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!selectedItem || !selectedBom ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="h4">
                    Please Select an Item and BOM to View Component Data
                  </Typography>
                </TableCell>
              </TableRow>
            ) : bomCompsFetching ? (
              <>
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading Components, Please Wait... <CircularProgress />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={1}>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={1}>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={1}>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                      <Typography variant="body1">
                        <Skeleton variant="text" />
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <Skeleton variant="text" />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                </TableRow>
              </>
            ) : mainBomComps.length === 0 ? (
              <></>
            ) : (
              mainBomComps.map((val, ind) => (
                <ComponentLine
                  key={ind}
                  level={1}
                  thisCompData={val}
                  parentItem={selectedItem?.value.number || "UNKNOWN ITEM"}
                  parentQuant={targetQuant}
                  totalNeeded={totalNeeded}
                  setTotalNeeded={handleTotalNeededUpdate}
                  generationId={generationIdRef.current}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default PageDemandForecast;
